<template>
<v-container fluid>

    <!-- Page Title -->
    <app-page-title
      v-bind:title="'Gallery Media'"
      v-bind:icon="'perm_media'"
    />

    <!-- Media Table -->
    <v-data-table
      v-model="selected"
      :headers="headers"
      :items="filteredMedia"
      :loading="isLoading"
      :no-data-text="noDataText"
      :search="search"
      item-key="filename"
      show-select
      mobile-breakpoint="500"
      >
      <template v-slot:top>

        <!-- Header Toolbar -->
        <v-toolbar dark :color="appColor_Medium" class="mt-2">

          <!-- Search Text -->
          <v-text-field
            v-model="search"
            append-icon="search"
            label="Search"
            :disabled="isLoading"
            single-line
            hide-details
            clearable
            >
          </v-text-field>

          <v-spacer />

          <!-- Upload Media Buttons -->
          <template v-if="mediaPath === mediaPath_Photos">
            <v-btn
              outlined
              :small="isSmallDevice"
              @click="$refs.photos.click()"
              :disabled="isDeleting || isLoading"
              >
              Upload Photos
            </v-btn>
            <input v-show="false" type="file" :ref="mediaPath_Photos" multiple accept='image/*' @change="fileChanged(mediaPath_Photos)" >
          </template>
          
          <template v-else>
            <v-btn
              outlined
              @click="$refs.videos.click()"
              :disabled="isDeleting || isLoading"
              >
              Upload Videos
            </v-btn>
            <input v-show="false" type="file" :ref="mediaPath_Videos" multiple accept='video/mp4' @change="fileChanged(mediaPath_Videos)" > 
          </template>
        </v-toolbar>

        <v-row no-gutters class="mt-2">

          <!-- Media Typa Radios -->
          <v-col cols="12" md="3">
            <v-alert outlined min-width="230" color="grey lighten-1" class="pt-2 pb-3 ml-2 mr-3">
              <v-radio-group
                v-model="mediaPath"
                row
                hide-details
                @change="selected = []"
                :disabled="isLoading"
                class="my-0"
                >
                <template v-for="(type, i) in mediaPaths">
                  <v-radio :key="i" :label="type.text" :value="type.value"></v-radio>
                </template>
              </v-radio-group>
            </v-alert>
          </v-col>

          <!-- Source Dropdown -->
          <v-col cols="12" md="4">
            <v-select
              v-model="selectedSource"
              :items="sources"
              item-value="cosmosCode"
              item-text="name"
              label="Source"
              :disabled="isLoading"
              @change="sourceChanged"
              class="mx-3"
              >
            </v-select>
          </v-col>

          <!-- Breed Dropdown -->
          <v-col cols="12" md="4">
            <v-select
              v-model="selectedBreed"
              :items="sourceBreeds"
              item-value="shortName"
              item-text="name"
              label="Breed"
              :disabled="isLoading"
              @change="selected = []"
              class="mx-2"
              >
            </v-select>
          </v-col>

          <!-- Delete Button -->
          <v-col cols="12" md="1">
            <v-btn
              large
              color="error"
              :loading="isDeleting"
              :disabled="isNoMediaSelected || isLoading"
              @click="remove"
              class="mt-2 ml-3"
              >
              <v-icon>delete</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </template>

      <template v-slot:item.thumbnail="{ item }">
        <router-link to="/mediaManager">
          <span @click="preview(item)" class="text-center">
            <v-img width="50" height="50" :src="getThumbnailUrl(item)"></v-img>
          </span>
        </router-link>
      </template>

      <template v-slot:item.filename="{ item }">
        <span>
          {{ getName(item.filename, 1) }}
        </span>
      </template>

      <template v-slot:item.size="{ item }">
        <span>
          {{ getFormattedSize(item.size, 1) }}
        </span>
      </template>

      <template v-slot:item.createDate="{ item }">
        <span>
          {{ getDate(item.createDate) }}
        </span>
      </template>

    </v-data-table>

    <!-- Media Previewer -->
    <v-dialog v-model="previewDialog" max-width="800px" persistent>
      <media-previewer
        v-bind:filename="previewFilename"
        v-bind:type="previewType"
        v-bind:url="previewUrl"
        v-on:close="closePreview"
        >
      </media-previewer>
    </v-dialog>

    <!-- Upload Progress Dialog -->
    <v-dialog v-model="uploadDialog" max-width="400px" :persistent="true">
      <v-card class="pa-1">
        <v-card-title class="px-3 pb-0">
          <span class="body-2 font-weight-medium">{{ progressMessage }}</span>
        </v-card-title>
        <v-card-text class="pa-3">
          <v-progress-linear
            height="20"
            rounded
            :color="appColor_Progress"
            :value="uploadPercentage"
            >
          </v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Error Dialog -->
    <v-dialog v-model="errorDialog" max-width="500px">
      <app-error
        v-bind:errorTitle="errorTitle"
        v-bind:errorText="errorText"
        v-on:close="errorDialog = false"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'
import appService from '@/app.service'
import AppPageTitle from '@/components/AppPageTitle'
import MediaPreviewer from '@/components/MediaPreviewer'
import AppError from '@/components/AppError'
import { AppColor, MediaPath } from '@/config.js'

export default {
  name: "MediaManager",
  components: {
    AppPageTitle,
    MediaPreviewer,
    AppError
  },
  data() {
    return {
      // colors
      appColor_Medium: AppColor.Medium,
      appColor_ActionButton: AppColor.ActionButton,
      appColor_Progress: AppColor.Progress,

      // media paths
      mediaPath_Photos: MediaPath.Photos,
      mediaPath_Videos: MediaPath.Videos,

      uri: null,
      container: null,

      isLoading: false,
      isDeleting: false,
      search: null,

      selected: [],
      sources: [],
      sourcesBreeds: [],
      media: [],

      headers: [
        {
          value: 'thumbnail',
          align: 'center',
          width: '5%',
          sortable: false,
          class: "pr-0"
        },
        {
          text: 'Name',
          value: 'filename',
          align: 'left',
          width: '45%'
        },
        {
          text: 'Type',
          value: 'contentType',
          align: 'left',
          width: '15%'
        },
        {
          text: 'Size',
          value: 'size',
          align: 'left',
          width: '15%'
        },
        {
          text: 'Created',
          value: 'createDate',
          align: 'left',
          width: '20%'
        }
      ],

      selectedSource: null,
      selectedBreed: null,

      mediaPaths: [
        {
          value: MediaPath.Photos,
          text: 'Photos'
        },
        {
          value: MediaPath.Videos,
          text: 'Videos'
        }
      ],
      mediaPath: MediaPath.Photos,

      // preview
      previewDialog: false,
      previewFilename: null,
      previewType: null,
      previewUrl: null,

      // upload
      uploadFiles: [],
      uploadDialog: false,
      uploadPercentage: 0,
      progressMessage: null,

      // error handling
      errorDialog: false,
      errorTitle: 'Internal Error',
      errorText: null    
    }
  },
  computed: {
    baseUrl () {
      return `${this.uri}/${this.container}/${this.mediaPath}/${this.selectedSource}/${this.selectedBreed}`
    },
    sourceBreeds () {
      if (this.selectedSource === null) return []
      const source = this.selectedSource
      const srcId = this.sources.filter(src => { return src.cosmosCode === source })[0].id
      return this.sourcesBreeds.filter(src => { return src.animalSourceId === srcId })
    },
    filteredMedia () {
      const path = this.mediaPath
      const src = this.selectedSource
      const breed = this.selectedBreed
      return this.media.filter(m => {
          return m.path === path && m.source === src && m.breed === breed
        })
    },
    noDataText () {
      if (this.isLoading) return 'One moment...'

      return this.filteredMedia.length === 0
        ? this.mediaPath === this.mediaPath_Photos ? 'No photos found' : 'No videos found'
        : ''
    },
    isNoMediaSelected () {
      return this.selected.length === 0
    },
    isSmallDevice () {
      return this.$vuetify.breakpoint.name === 'xs' || this.$vuetify.breakpoint.name  === 'sm'
    }
  },
  methods: {
    loadData () {
      const self = this

      self.isLoading = true
      appService.getMedia().then(data => {
        if (data.errorSeverity > 0) {
          self.errorMessage = data.errorMessage
          self.errorDialog = true
        } else {
          self.uri = data.uri
          self.container = data.container
          self.sources = data.sources
          self.sourcesBreeds = data.sourcesBreeds
          self.media = data.media
          self.selectedSource = data.sources.length > 0
            ? data.sources[0].cosmosCode
            : null
          self.selectedBreed = self.sourceBreeds.length > 0
            ? self.sourceBreeds[0].shortName
            : null
        }
        self.isLoading = false
      })
    },

    fileChanged (ref) {
      const self = this
      const files = self.$refs[ref].files
      self.uploadFiles = files

      // determine if any filenames already exist
      var uploadFilenames = []
      for (var key in files) {
        uploadFilenames.push(files[key].name)
      }

      const duplicateMedia = self.filteredMedia.filter(m => {
        return uploadFilenames.filter(f => { 
            return f === m.filename 
          }).length > 0
        })

      // if any duplicate filenames prompt to overwrite
      if (duplicateMedia.length > 0) {
        var duplicateFilesText = ''
        duplicateMedia.forEach(m => {
          duplicateFilesText = duplicateFilesText + '</br>' + m.filename
        })
        self.$confirm('<p class="body-1 pt-5 mb-0">The following file(s) already exist:</p>' +
          '<p class="body-1">' + duplicateFilesText + '</p><p class="title font-weight-bold">Overwrite?</p>', {
          color: 'yellow darken-3',
          icon: 'warning',
          title: 'File(s) Exist',
          width: 300
        }).then(res => {
          if (res) {
            // upload and overwrite duplicates
            self.upload(duplicateMedia)
          } else {
            self.resetInputs()
          }
        })
      } else {
        // no duplicates found - just upload
        self.upload()
      }
    },

    upload (duplicateMedia) {
      const self = this

      const files = self.uploadFiles
      const path = self.mediaPath
      const src = self.selectedSource
      const breed = self.selectedBreed

      self.progressMessage = `Uploading ${self.uploadFiles.length} file(s)...`

      let formData = new FormData()
      for (var key in files) {
        const file = files[key]
        formData.append('file', file)
      }
      // derive the url according to media type
      const baseUrl = `media/upload/${path}`
      var url = baseUrl

      // photos - pass source & breed as url segments
      if (path === self.mediaPath_Photos) {
        url = `${baseUrl}/${src}/${breed}`

      // videos - pass source & breed as form data
      } else if (path === self.mediaPath_Videos ) {
        formData.append('source', src)
        formData.append('breed', breed)
      }
      if (url === null) return

      self.uploadDialog = true
      axios.post(url,
        formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: function (progressEvent) {
            self.uploadPercentage = parseInt(Math.round((progressEvent.loaded * 100) / progressEvent.total))
            if (self.uploadPercentage >= 100) {
              setTimeout(function() {
                self.progressMessage = 'Finalizing...'
              }, 500)
            }
          }
        }
      ).then(response => {
        self.progressMessage = 'Complete'
        self.uploadDialog = false
        self.uploadPercentage = 0
        self.numFiles = 0
        const data = response.data
        if (data.errorSeverity > 0) {
          self.errorTitle = 'Upload Error'
          self.errorText = data.errorMessage
          self.errorDialog = true
        } else {
          data.uploadResults.forEach(result => {
            if (result.success) {
              if (!self.isNullOrUndefined(duplicateMedia)) {
                duplicateMedia.forEach(item => {
                  const idx = self.media.indexOf(item)
                  self.media.splice(idx, 1)
                })
              }
              self.media.unshift(result.mediaItem)
            }
          })
          self.resetInputs()
        }
      }).catch(() => {
        self.resetInputs()
      })
    },

    remove () {
      const self = this
      const mediaType = self.mediaPath === self.mediaPath_Photos
        ? 'photo' : 'video'
      const mediaText = self.selected.length === 1
        ? mediaType : `${mediaType}s`

      self.$confirm(`<p class="subtitle-1 pt-5">You have <b> ${self.selected.length} </b> ${mediaText} selected.</p>`, {
        color: 'red darken-3',
        icon: 'help',
        title: `Delete ${mediaText}?`,
        width: 300
      }).then(res => {
        if (res) {
          self.confirmDialog = false
          self.isDeleting = true
          var deleteItems = []
          self.selected.forEach(item => {
            deleteItems.push(`${item.path}/${item.source}/${item.breed}/${item.filename}`)
          })

          appService.deleteMediaBulk(self.container, deleteItems).then((data) => {
            if (data.errorSeverity > 0) {
              self.errorTitle = 'Delete Error'
              self.errorText = data.errorMessage
              self.errorDialog = true
            } else {
              self.selected.forEach(item => {
                const idx = self.media.indexOf(item)
                self.media.splice(idx, 1)
              })
            }
            self.selected = []
            self.isDeleting = false
          })
        }
      })
    },

    preview (item) {
      this.previewFilename = this.getName(item.filename)
      this.previewUrl =  `${this.baseUrl}/${item.filename}`
      this.previewType = item.path
      this.previewDialog = true
    },

    closePreview () {
      this.previewFilename = null
      this.previewType = null
      this.previewUrl = null
      this.previewDialog = false
    },

    getThumbnailUrl (item) {
      const ext = item.filename.split('.').pop()
      const url = `${this.baseUrl}/thumbnail/${item.filename.replace(ext, 'jpg')}`
      return url
    },

    getName (filename) {
      if (filename !== null) {
        const parts = filename.split('.')
        return parts[0]
      }
    },

    getDate (date) {
      if (date !== undefined) {
        if (date.length > 0) {
          return this.$moment(date).format('MMM-DD-YYYY') // @h:mm A')
        }
      }
      return null
    },

    getFormattedSize (bytes, decimals) {
      if (this.isNullOrUndefined(bytes)) return ''
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

      for (var i = 0, r = bytes, b = 1024; r > b; i++) r /= b
      return `${parseFloat(r.toFixed(decimals))} ${sizes[i]}`
    },

    getMediaType (contentType) {
      if (contentType !== null) {
        return contentType.substring(0, contentType.lastIndexOf('/'))
      }
    },

    resetInputs () {
      if (!this.isNullOrUndefined(this.$refs.photos)) {
        this.$refs.photos.type = 'text'
        this.$refs.photos.type = 'file'
      }
      if (!this.isNullOrUndefined(this.$refs.videos)) {
        this.$refs.videos.type = 'text'
        this.$refs.videos.type = 'file'
      }
    },

    isNullOrUndefined (obj) {
      return obj === undefined 
        || typeof obj === 'undefined' 
        || obj === null
    },
    
    // event handlers
    sourceChanged () {
      this.selectedBreed = this.sourceBreeds.length > 0
        ? this.sourceBreeds[0].shortName
        : null
      this.selected = []
    }
  },
  created () {
    this.loadData()
  }
}
</script>